import React, { useState, useEffect } from 'react';
import { FaTimes, FaPaperPlane, FaCheck, FaStar, FaUser, FaEnvelope, FaPhone, FaComment, FaShieldAlt, FaWrench } from 'react-icons/fa';
import { MdVerified } from 'react-icons/md';
import { createHtmlTemplate } from '../api/TemplateService';
import { sendHtmlEmail } from '../api/emailapi.js';

const EnquiryPopup = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    subject: '',
    message: '',
    agreeTerms: false,
    receiveUpdates: false
  });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = 'auto';
    };
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Create email body using template
      const emailBody = `
        <!DOCTYPE html>
        <html>
        <head>
          <style>
            body { font-family: Arial, sans-serif; line-height: 1.6; color: #333; }
            .container { max-width: 600px; margin: 0 auto; padding: 20px; }
            .header { background: linear-gradient(135deg, #dc2626, #b91c1c); color: white; padding: 20px; text-align: center; border-radius: 10px 10px 0 0; }
            .content { background: #f9fafb; padding: 20px; border: 1px solid #e5e7eb; border-top: none; border-radius: 0 0 10px 10px; }
            .field { margin-bottom: 15px; }
            .label { font-weight: bold; color: #dc2626; margin-bottom: 5px; display: block; }
            .value { background: white; padding: 10px; border-radius: 5px; border: 1px solid #e5e7eb; }
            .badge { background: #dc2626; color: white; padding: 3px 8px; border-radius: 5px; font-size: 12px; display: inline-block; margin-top: 10px; }
            hr { border: none; border-top: 1px solid #e5e7eb; margin: 15px 0; }
          </style>
        </head>
        <body>
          <div class="container">
            <div class="header">
              <h2 style="margin:0"> New Enquiry Received</h2>
              <p style="margin:5px 0 0; opacity:0.9">Ambris Service Center</p>
            </div>
            <div class="content">
              <div class="field">
                <div class="label"> Full Name</div>
                <div class="value">${formData.fullName}</div>
              </div>
              <div class="field">
                <div class="label"> Email Address</div>
                <div class="value">${formData.email}</div>
              </div>
              <div class="field">
                <div class="label">📞 Phone Number</div>
                <div class="value">${formData.phone}</div>
              </div>
              <div class="field">
                <div class="label"> Subject</div>
                <div class="value">${formData.subject}</div>
              </div>
              <div class="field">
                <div class="label"> Message</div>
                <div class="value">${formData.message || 'No message provided'}</div>
              </div>
              <hr>
              <div class="field">
                <div class="label"> Terms & Conditions</div>
                <div class="value">${formData.agreeTerms ? '✓ Agreed' : '✗ Not agreed'}</div>
              </div>
              <div class="field">
                <div class="label"> Receive Updates</div>
                <div class="value">${formData.receiveUpdates ? '✓ Yes' : '✗ No'}</div>
              </div>
              <div class="badge"> Enquiry from Ambris Service Center Website</div>
            </div>
          </div>
        </body>
        </html>
      `;

      const result = await sendHtmlEmail(
        "info@ambrisservice.com", 
        `New Enquiry from ${formData.fullName} - ${formData.subject}`, 
        emailBody
      );
      
      if (result.success) {
        console.log('Form submitted successfully:', formData);
        setIsSubmitted(true);
        
        // Reset form after successful submission
        setTimeout(() => {
          setIsSubmitted(false);
          setIsOpen(false);
          document.body.style.overflow = 'auto';
          setFormData({
            fullName: '',
            email: '',
            phone: '',
            subject: '',
            message: '',
            agreeTerms: false,
            receiveUpdates: false
          });
        }, 2000);
      } else {
        throw new Error("Email sending failed");
      }
    } catch (error) {
      console.error("Email failed:", error);
      alert("Failed to send enquiry ❌\nPlease try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    document.body.style.overflow = 'auto';
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="relative bg-white rounded-xl shadow-2xl w-full max-w-sm animate-slideUp">
          
          {/* Decorative Red Border Top */}
          <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-red-500 via-red-600 to-red-700 rounded-t-xl"></div>
          
          {/* Header with Red Gradient */}
          <div className="bg-gradient-to-r from-red-600 via-red-500 to-red-700 text-white px-4 py-3 rounded-t-xl">
            <div className="flex justify-between items-start">
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <FaWrench className="text-yellow-200 text-sm" />
                  <h2 className="text-base font-bold tracking-wide">Ambris Service Center</h2>
                </div>
                <p className="text-xs text-red-100">Get in Touch with Us</p>
              </div>
              <button 
                onClick={handleClose}
                className="hover:bg-white/20 p-1 rounded-full transition-colors"
              >
                <FaTimes className="text-white text-sm" />
              </button>
            </div>
          </div>

          {/* Success Message */}
          {isSubmitted && (
            <div className="m-3 bg-green-50 border border-green-200 rounded-lg p-2 flex items-center gap-2">
              <div className="bg-gradient-to-r from-green-500 to-emerald-500 rounded-full p-0.5">
                <FaCheck className="text-white text-[10px]" />
              </div>
              <p className="text-xs font-semibold text-green-800">Enquiry Sent Successfully!</p>
            </div>
          )}

          {/* Form */}
          <form onSubmit={handleSubmit} className="p-4 space-y-3">
            {/* Full Name */}
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Full Name <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaUser className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500 text-xs" />
                <input
                  type="text"
                  name="fullName"
                  value={formData.fullName}
                  onChange={handleChange}
                  required
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all"
                  placeholder="Enter your full name"
                />
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Email Address <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaEnvelope className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500 text-xs" />
                <input
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all"
                  placeholder="your@email.com"
                />
              </div>
            </div>

            {/* Phone */}
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Phone Number <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <FaPhone className="absolute left-3 top-1/2 -translate-y-1/2 text-red-500 text-xs" />
                <input
                  type="tel"
                  name="phone"
                  value={formData.phone}
                  onChange={handleChange}
                  required
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all"
                  placeholder="+91 8081321406"
                />
              </div>
            </div>

            {/* Subject */}
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Select Subject <span className="text-red-500">*</span>
              </label>
              <select
                name="subject"
                value={formData.subject}
                onChange={handleChange}
                required
                className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all bg-white"
              >
                <option value="">Select a subject</option>
                <option value="bike_service">Bike Service Inquiry</option>
                <option value="car_service">Car Service Inquiry</option>
                <option value="emergency">Emergency Service</option>
                <option value="feedback">Feedback</option>
                <option value="other">Other</option>
              </select>
            </div>

            {/* Message */}
            <div>
              <label className="block text-gray-700 font-semibold text-xs mb-1">
                Your Message
              </label>
              <div className="relative">
                <FaComment className="absolute left-3 top-3 text-red-500 text-xs" />
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="2"
                  className="w-full pl-8 pr-3 py-1.5 text-sm border border-gray-300 rounded-lg focus:outline-none focus:border-red-400 focus:ring-2 focus:ring-red-400/20 transition-all resize-none"
                  placeholder="Tell us about your vehicle or service requirement..."
                ></textarea>
              </div>
            </div>

            {/* Checkboxes */}
            <div className="space-y-2">
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="agreeTerms"
                  checked={formData.agreeTerms}
                  onChange={handleChange}
                  required
                  className="mt-0.5 w-3 h-3 text-red-500 focus:ring-red-400 border-gray-300 rounded"
                />
                <span className="text-xs text-gray-700 leading-tight">
                  I agree to the <a href="#" className="text-red-600 hover:underline font-medium">Terms & Conditions</a> and <a href="#" className="text-red-600 hover:underline font-medium">Privacy Policy</a>.
                </span>
              </label>
              
              <label className="flex items-start gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  name="receiveUpdates"
                  checked={formData.receiveUpdates}
                  onChange={handleChange}
                  className="mt-0.5 w-3 h-3 text-red-500 focus:ring-red-400 border-gray-300 rounded"
                />
                <span className="text-xs text-gray-600 leading-tight">
                  I would like to receive updates from Ambris Service Center via Email, SMS, WhatsApp, and RCS.
                </span>
              </label>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full bg-gradient-to-r from-red-500 via-red-600 to-red-700 hover:from-red-600 hover:via-red-700 hover:to-red-800 text-white font-semibold py-2 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                  SENDING...
                </>
              ) : (
                <>
                  <FaPaperPlane className="text-white text-xs" />
                  SEND ENQUIRY
                </>
              )}
            </button>

            {/* Trust Badge */}
            <div className="text-center pt-1">
              <div className="flex items-center justify-center gap-1">
                <FaShieldAlt className="text-gray-400 text-[10px]" />
                <p className="text-[10px] text-gray-400">Your information is safe with us</p>
                <MdVerified className="text-red-500 text-[10px]" />
              </div>
            </div>
          </form>

          {/* Decorative Bottom Border */}
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-red-500 via-red-600 to-red-700 rounded-b-xl"></div>
        </div>
      </div>

      <style>{`
        @keyframes slideUp {
          from {
            transform: translateY(20px);
            opacity: 0;
          }
          to {
            transform: translateY(0);
            opacity: 1;
          }
        }
        
        .animate-slideUp {
          animation: slideUp 0.3s ease-out;
        }

        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .animate-spin {
          animation: spin 0.8s linear infinite;
        }
      `}</style>
    </>
  );
};

export default EnquiryPopup;